import React, { useState, useContext, useRef, useEffect } from 'react';

import { NotificationManager } from 'react-notifications';
import { useNavigate } from 'react-router-dom';

/** images */
import bankPlacecardImg from '../../assets/images/amazon/bankPlacecard.svg';
import TenantConfig from '../../config';

/** context */
import { UserContext } from '../../context/UserContext';
import { sanitizeHtml } from '../../helpers/format';
import { useRudder } from '../../hooks/rudder';
import { useForm } from '../../hooks/useForm';

/** service */
import { updateApplication, validateProperty, validateRoutingNumber } from '../../service/api';

/** components */
import { FileInput } from '../FormElements';

const NeedDocs = ({ handleContinue, handleGoBackSection, plaidState, isEdit, setCloseTimes }) => {
  const { appData, updateAppData, setLoading } = useContext(UserContext);
  const { errorEvent } = useRudder();
  const navigate = useNavigate();
  const { getPropertyFields } = useForm();
  const [data, setData] = useState({}); // For file fields
  const [dataError, setDataError] = useState({}); // For file fields
  const [amazonCheck, setAmazonCheck] = useState(false);
  const [bankAccountCheck, setBankAccountCheck] = useState(false);
  const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [checkboxError, setCheckboxError] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [inputData, setInputData] = useState({
    associatedCustomerName: '',
    accountNumber: '',
    routingNumber: '',
    accountNumberConfirm: '',
    routingNumberConfirm: '',
    bankAccountType: '',
  }); // For input fields
  const [inputErrors, setInputErrors] = useState({}); // For input fields
  const [routingMatchSource, setRoutingMatchSource] = useState(null);

  const bankAccountTypes = ['CHECKING', 'SAVINGS'];

  const fileFeature = appData?.applicationFeatures?.find(
    (feature) => feature.itemType === 'FileProperty'
  );
  const financialInstrumentFeature = appData?.applicationFeatures?.find(
    (feature) => feature.itemType === 'FinancialInstrumentProperty'
  );

  const stateRef = useRef();
  stateRef.current = data;

  useEffect(() => {
    if (fileFeature && fileFeature.format) {
      let fileData = {};
      const originalFileTypes = Object.keys(fileFeature.format);
      fileFeature.value
        .filter((field) => field.fileType.includes('bankVerificationStatement'))
        .map((item) => {
          if (originalFileTypes.includes(item['fileType'])) {
            // For the fields that exist under feature.format
            if (item['category']) {
              if (!fileData[item['fileType']]) {
                fileData[item['fileType']] = [];
              }
              fileData[item['fileType']].push({
                ...item,
                isProtected: item.password == 'masked' ? '1' : '0',
                password: null,
                isPwdShow: '0',
              });
            } else {
              if (!fileData[item['fileType']]) {
                fileData[item['fileType']] = [];
              }
              fileData[item['fileType']].push({
                ...item,
                isProtected: item.password == 'masked' ? '1' : '0',
                password: null,
                isPwdShow: '0',
              });
            }
          } else {
            // For the fields that only exist under feature.value
            fileData[item['fileName']] = [
              {
                ...item,
                isProtected: item.password == 'masked' ? '1' : '0',
                password: null,
                isPwdShow: '0',
              },
            ];
          }
        });
      setData(fileData);
    }
    // eslint-disable-next-line
  }, [fileFeature]);

  const fields = getPropertyFields(fileFeature, inputData).filter((field) =>
    field.fileType.includes('bankVerificationStatement')
  );

  useEffect(() => {
    if (financialInstrumentFeature && financialInstrumentFeature.associatedCustomerName) {
      setInputData({
        ...inputData,
        associatedCustomerName: financialInstrumentFeature.associatedCustomerName,
      });
    }
    window.scroll(0, 0);
  }, [financialInstrumentFeature.associatedCustomerName]);

  useEffect(() => {
    if (financialInstrumentFeature && financialInstrumentFeature.bankAccountType) {
      setInputData({ ...inputData, bankAccountType: financialInstrumentFeature.bankAccountType });
    }
  }, [financialInstrumentFeature.bankAccountType]);

  useEffect(() => {
    if (bankAccountCheck && amazonCheck) {
      setCheckboxError('');
    }
    setSubmitButtonDisabled(checkIfSubmitButtonDisabled(amazonCheck, bankAccountCheck));
  }, [bankAccountCheck, amazonCheck, inputErrors['routingNumber']]);

  const handleSubmit = async () => {
    setDataError({});
    setInputErrors({});
    const sanitizedData = Object.values(stateRef.current).reduce(
      (previousValue, currentValue) => [...previousValue, ...currentValue],
      []
    );

    const formData =
      fields && fields.length > 0
        ? [
            {
              itemType: fileFeature.itemType,
              dataType: fileFeature.dataType,
              name: fileFeature.name,
              value: sanitizedData.filter((item) => {
                return item.userAdded == false || item.id || (!item.error && !item.isCanceled);
              }),
            },
          ]
        : [];

    let filesValid = true;
    if (fields.length > 0) {
      fields.map((field) => {
        if (
          !data[field.name] ||
          (data[field.name].length && data[field.name][0].status == 'REQUESTED')
        ) {
          handleSetDataError(field, 'This file is required');
          filesValid = false;
        }
      });
    }

    if (plaidState === 'failure' || plaidState === 'manual' || isEdit) {
      if (
        !inputData.accountNumber ||
        !inputData.routingNumber ||
        !inputData.accountNumberConfirm ||
        !inputData.routingNumberConfirm ||
        !inputData.bankAccountType ||
        !inputData.associatedCustomerName
      ) {
        if (!inputData.associatedCustomerName) {
          setInputErrors((dataError) => ({
            ...dataError,
            ['associatedCustomerName']: 'Account Holder Name is required',
          }));
        }
        if (!inputData.accountNumber) {
          setInputErrors((dataError) => ({
            ...dataError,
            ['accountNumber']: 'Business Account Number is required',
          }));
        }
        if (!inputData.routingNumber) {
          setInputErrors((dataError) => ({
            ...dataError,
            ['routingNumber']: 'Business Routing Number is required',
          }));
        }
        if (!inputData.accountNumberConfirm) {
          setInputErrors((dataError) => ({
            ...dataError,
            ['accountNumberConfirm']: 'Please confirm the Business Account Number.',
          }));
        }
        if (!inputData.routingNumberConfirm) {
          setInputErrors((dataError) => ({
            ...dataError,
            ['routingNumberConfirm']: 'Please confirm Business Routing Number.',
          }));
        }
        if (!inputData.bankAccountType) {
          setInputErrors((dataError) => ({
            ...dataError,
            ['bankAccountType']: 'Please choose the Bank Account Type.',
          }));
        }
        return;
      }
      if (inputData.routingNumber.length != 9) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['routingNumber']: 'Routing number should have 9 digits.',
        }));
        return;
      }
      if (inputData.accountNumber.length > 18) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['accountNumber']: 'Account number should have less than 18 digits',
        }));
        return;
      }

      if (inputData.accountNumber.length < 3) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['accountNumber']: 'Account number should have more than 2 digits',
        }));
        return;
      }

      if (inputData.routingNumberConfirm.length > 9) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['routingNumberConfirm']: 'Routing number should have less than 10 digits.',
        }));
        return;
      } else if (inputData.routingNumberConfirm !== inputData.routingNumber) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['routingNumber']: 'Routing number doesn’t match.',
          ['routingNumberConfirm']: 'Routing number doesn’t match.',
        }));
        return;
      } else {
        setInputErrors((dataError) => ({
          ...dataError,
          ['routingNumber']: '',
          ['routingNumberConfirm']: '',
        }));
      }

      if (inputData.accountNumberConfirm.length > 20) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['accountNumberConfirm']: 'Max length for Business Account Number is 20',
        }));
        return;
      } else if (inputData.accountNumberConfirm !== inputData.accountNumber) {
        setInputErrors((dataError) => ({
          ...dataError,
          ['accountNumber']: 'Account number doesn’t match.',
          ['accountNumberConfirm']: 'Account number doesn’t match.',
        }));
        return;
      } else {
        setInputErrors((dataError) => ({
          ...dataError,
          ['accountNumber']: '',
          ['accountNumberConfirm']: '',
        }));
      }

      formData.push({
        itemType: 'FinancialInstrumentProperty',
        dataType: 'financialinstrument',
        name: 'bankAccount',
        value: {
          associatedCustomerName: inputData.associatedCustomerName,
          accountNumber: inputData.accountNumber,
          routingNumber: inputData.routingNumber,
          bankAccountType: inputData.bankAccountType,
        },
      });

      formData.push({
        itemType: 'PlaidTokenProperty',
        dataType: 'plaidtoken',
        name: 'plaidToken',
        value: {
          plaidLinkage: {
            plaidNotLinkable: true,
          },
        },
      });
    }

    if (!filesValid) {
      return;
    }

    try {
      setLoading(true);
      const updatedData = await updateApplication(formData);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }

      const validationData = await validateProperty('file,bankAccount,plaidToken');

      if (validationData && validationData.data) {
        if (validationData.data.errors?.length) {
          errorEvent('Error API', validationData.data.errors);
          console.log(validationData.data.errors?.length);
          let errors1 = new Array(
            fields.filter((field) => field.fileType.includes('bankVerificationStatement')).length
          ).fill('');

          validationData.data.errors.map((error) => {
            const attributes = error.property.split('.');
            if (attributes.length == 1) {
              /** section level error */
              setSubmissionError(error.message);
            } else {
              const errorFileIndex = files.findIndex((f) => f.fileType === attributes[1]);
              if (errorFileIndex > -1) {
                errors1[errorFileIndex] = error.message;
              }
            }
          });
          setDataError(errors1);
          setLoading(false);
          return false;
        }
      }
    } catch (e) {
      NotificationManager.warning("Bank details couldn't be saved", 'Warning');
      console.log(e);
      errorEvent('Error API', e);
    }

    setLoading(false);
    if (isEdit) {
      navigate('/review');
    }
    handleContinue();
  };

  const checkIfSubmitButtonDisabled = (isAmazonCheck, isBankAccountCheck) => {
    if (inputErrors['routingNumber']) {
      return true;
    }
    if (TenantConfig?.selectBank.renderAmazonBankOptions) {
      return !isBankAccountCheck || !isAmazonCheck;
    } else {
      return !isBankAccountCheck;
    }
  };

  /** call updateApplication API for FileInput */
  const handleSaveFileProperty = async () => {
    setTimeout(async () => {
      // To call update API with the updated state data, delay api call
      const sanitizedData = Object.values(stateRef.current).reduce(
        (previousValue, currentValue) => [...previousValue, ...currentValue],
        []
      );

      const updatedData = await updateApplication([
        {
          itemType: fileFeature.itemType,
          dataType: fileFeature.dataType,
          name: fileFeature.name,
          value: sanitizedData.filter((item) => {
            return item.userAdded == false || item.id || (!item.error && !item.isCanceled);
          }),
        },
      ]);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }
    }, 1000);
  };

  // update files state for FileInput
  const handleSetData = (field, values) => {
    setData((inputData) => ({
      ...inputData,
      [field.name]: Array.isArray(values) ? [...values] : values,
    }));
  };

  const handleSetDataError = (field, msg) => {
    setDataError((dataError) => ({ ...dataError, [field.name]: msg }));
  };

  const initializeSectionError = () => {};

  const handleChangeNumber = (value, fieldName) => {
    setInputData({ ...inputData, [fieldName]: value.replace(/\D/g, '') });
    // setInputErrors((dataError) => ({ ...dataError, [fieldName]: '' }));
  };

  const goBackToSection = () => {
    handleGoBackSection();
    setCloseTimes(0);
  };

  const handleValidateRoutingNumber = async (value) => {
    const res = await validateRoutingNumber(value);
    if (res && res.data && res.data?.hits && res.data.hits.hits?.length) {
      const hit = res.data.hits.hits[0];
      setInputErrors((dataError) => ({
        ...dataError,
        ['routingNumber']: '',
      }));
      setRoutingMatchSource(hit._source);
      return;
    }
    setRoutingMatchSource(null);
    setInputErrors((dataError) => ({
      ...dataError,
      ['routingNumber']: 'No Bank matched this routing number',
    }));
  };

  return (
    <div className="need-docs">
      <div className="need-docs-header">
        <h1 className="title">{TenantConfig?.manualBankAccount?.title}</h1>
        <h3
          className="subtitle"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(TenantConfig?.manualBankAccount?.subTitle),
          }}
        ></h3>
        <button className="primary-btn" onClick={() => goBackToSection()}>
          I want to try to use PLAID again
        </button>
      </div>
      {fields.map((field) => {
        if (!data[field.name]) {
          return;
        }
        return (
          <FileInput
            field={field}
            key={field.name}
            fieldValue={data[field.name] ?? []}
            fieldError={dataError[field.name]}
            handleSetData={handleSetData}
            handleSetDataError={handleSetDataError}
            handleSaveFileProperty={handleSaveFileProperty}
            initializeSectionError={initializeSectionError}
          />
        );
      })}
      {(plaidState === 'failure' || plaidState === 'manual' || isEdit) && (
        <div className="form-list">
          <div
            className={
              'form-group need-docs-grid-full-width ' +
              (inputErrors['associatedCustomerName'] ? 'error' : '')
            }
          >
            <div className="form-field">
              <label htmlFor="associatedCustomerName">Account Holder Name*</label>
              <input
                id="associatedCustomerName"
                placeholder=""
                value={inputData.associatedCustomerName}
                name="associatedCustomerName"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onChange={(e) => {
                  setInputData({ ...inputData, associatedCustomerName: e.target.value });
                  setInputErrors((dataError) => ({ ...dataError, ['associatedCustomerName']: '' }));
                }}
                spellCheck={false}
              />
              <p className="error-text-field">{inputErrors['associatedCustomerName']}</p>
            </div>
          </div>
          <div className={'form-group ' + (inputErrors['routingNumber'] ? 'error' : '')}>
            <div className="form-field">
              <label htmlFor="routingNumber" className="d-flex">
                <p className="circle routing-number" /> Your Routing Number*
              </label>
              <input
                id="routingNumber"
                placeholder={`xxxxxxxxxx${
                  financialInstrumentFeature && financialInstrumentFeature.maskedRoutingNumber
                    ? financialInstrumentFeature.maskedRoutingNumber
                    : 'xxx'
                }`}
                value={inputData.routingNumber}
                name="routingNumber"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onChange={(e) => {
                  handleChangeNumber(e.target.value, 'routingNumber');
                  if (e.target.value.length == 9) {
                    handleValidateRoutingNumber(e.target.value);
                  }
                }}
                onBlur={() => {
                  if (inputData['routingNumber'] == '') {
                    setRoutingMatchSource(null);
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['routingNumber']: 'Routing Number is required.',
                    }));
                  } else if (inputData['routingNumber'].length != 9) {
                    setRoutingMatchSource(null);
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['routingNumber']: 'Routing number should have 9 digits.',
                    }));
                  } else {
                    handleValidateRoutingNumber(inputData['routingNumber']);
                  }
                }}
                spellCheck={false}
              />
              <p className="error-text-field">{inputErrors['routingNumber']}</p>
              {routingMatchSource && (
                <p className="info">Bank Name: {routingMatchSource['Customer Name']}</p>
              )}
            </div>
          </div>
          <div className={'form-group ' + (inputErrors['accountNumber'] ? 'error' : '')}>
            <div className="form-field">
              <label htmlFor="accountNumber" className="d-flex">
                <p className="circle account-number" /> Your Account Number*
              </label>
              <input
                id="accountNumber"
                placeholder={`xxxxxxxxxx${
                  financialInstrumentFeature && financialInstrumentFeature.maskedAccountNumber
                    ? financialInstrumentFeature.maskedAccountNumber
                    : 'xxx'
                }`}
                value={inputData.accountNumber}
                name="accountNumber"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onChange={(e) => {
                  handleChangeNumber(e.target.value, 'accountNumber');
                }}
                onBlur={() => {
                  if (inputData['accountNumber'] == '') {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['accountNumber']: 'Account Number is required.',
                    }));
                  } else if (
                    inputData['accountNumber'].length < 2 ||
                    inputData['accountNumber'].length > 18
                  ) {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['accountNumber']:
                        'Account number should have less than 18 digits and more than 2 digits',
                    }));
                  } else {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['accountNumber']: '',
                    }));
                  }
                }}
                spellCheck={false}
              />
              <p className="error-text-field">{inputErrors['accountNumber']}</p>
            </div>
          </div>
          <div className={'form-group ' + (inputErrors['routingNumberConfirm'] ? 'error' : '')}>
            <div className="form-field">
              <label htmlFor="routingNumberConfirm">Confirm Routing Number*</label>
              <input
                id="routingNumberConfirm"
                placeholder={`xxxxxxxxxx${
                  financialInstrumentFeature && financialInstrumentFeature.maskedRoutingNumber
                    ? financialInstrumentFeature.maskedRoutingNumber
                    : 'xxx'
                }`}
                value={inputData.routingNumberConfirm}
                name="routingNumberConfirm"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onChange={(e) => {
                  handleChangeNumber(e.target.value, 'routingNumberConfirm');
                }}
                onBlur={() => {
                  if (inputData['routingNumberConfirm'] == '') {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['routingNumberConfirm']: 'Please confirm account number.',
                    }));
                  } else if (inputData['routingNumber'] != inputData['routingNumberConfirm']) {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['routingNumberConfirm']: 'Routing number does not match!',
                    }));
                  } else {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['routingNumberConfirm']: '',
                    }));
                  }
                }}
                spellCheck={false}
              />
              <p className="error-text-field">{inputErrors['routingNumberConfirm']}</p>
            </div>
          </div>
          <div className={'form-group ' + (inputErrors['accountNumberConfirm'] ? 'error' : '')}>
            <div className="form-field">
              <label htmlFor="accountNumberConfirm">Confirm Account Number*</label>
              <input
                id="accountNumberConfirm"
                placeholder={`xxxxxxxxxx${
                  financialInstrumentFeature && financialInstrumentFeature.maskedAccountNumber
                    ? financialInstrumentFeature.maskedAccountNumber
                    : 'xxx'
                }`}
                value={inputData.accountNumberConfirm}
                name="accountNumberConfirm"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onChange={(e) => {
                  handleChangeNumber(e.target.value, 'accountNumberConfirm');
                }}
                onBlur={() => {
                  if (inputData['accountNumberConfirm'] == '') {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['accountNumberConfirm']: 'Please confirm account number.',
                    }));
                  } else if (inputData['accountNumber'] != inputData['accountNumberConfirm']) {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['accountNumberConfirm']: 'Account number does not match!',
                    }));
                  } else {
                    setInputErrors((dataError) => ({
                      ...dataError,
                      ['accountNumberConfirm']: '',
                    }));
                  }
                }}
              />
              <p className="error-text-field">{inputErrors['accountNumberConfirm']}</p>
            </div>
          </div>
          <div
            className={`form-group need-docs-grid-full-width ${
              inputErrors['bankAccountType'] ? 'error' : ''
            }`}
          >
            <div className="form-field">
              <label htmlFor="bankAccountType">Bank Account Type*</label>
              <select
                id="bankAccountType"
                value={inputData['bankAccountType']}
                onChange={(e) => {
                  setInputData({ ...inputData, bankAccountType: e.target.value });
                  setInputErrors((dataError) => ({ ...dataError, ['bankAccountType']: '' }));
                }}
              >
                <option value="">Select</option>
                {bankAccountTypes.map((option, index) => (
                  <option value={option} key={index}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <p className="error-text-field">{inputErrors['bankAccountType']}</p>
          </div>
        </div>
      )}
      <div className="bank-place-card">
        <img src={bankPlacecardImg} alt="" />
      </div>

      <>
        {TenantConfig?.selectBank.renderAmazonBankOptions && (
          <div className="CustomCheckBox left">
            <div className="terms-checks">
              <label htmlFor="custom-checks-amazon-seller">
                <input
                  type="checkbox"
                  checked={amazonCheck}
                  id="custom-checks-amazon-seller"
                  name="business-checks"
                  onChange={() => {
                    setAmazonCheck(!amazonCheck);
                  }}
                />
                By checking here, you confirm that the bank account you selected above is the same
                as in Amazon Seller Central.
              </label>
            </div>
          </div>
        )}
        <div className="CustomCheckBox left">
          <div className="terms-checks">
            <label htmlFor="custom-checks-amazon">
              <input
                type="checkbox"
                checked={bankAccountCheck}
                id="custom-checks-amazon"
                name="business-checks"
                onChange={() => {
                  setBankAccountCheck(!bankAccountCheck);
                }}
              />
              By checking here, you confirm that this is the bank account you want to be funded.
            </label>
          </div>
        </div>
      </>

      {checkboxError && <p className="error-text checkbox-error">{checkboxError}</p>}
      {submissionError && <p className="error-text">{submissionError}</p>}
      <div className="actions">
        {isEdit && (
          <button className="primary-btn" onClick={() => navigate('/review')}>
            Cancel
          </button>
        )}
        <button className="primary-btn" onClick={handleSubmit} disabled={isSubmitButtonDisabled}>
          Continue
        </button>
      </div>
    </div>
  );
};

export default NeedDocs;
